/**
 * AG Grid Table Renderer
 *
 * Creates an AG Grid Enterprise data table from a DataTableConfig.
 * Uses a custom Quartz dark theme parameterized from DASHBOARD_TOKENS
 * to match the amCharts 5 chart aesthetic.
 */

import { createGrid, ModuleRegistry, AllCommunityModule, type ColDef, type GridOptions } from 'ag-grid-community';

ModuleRegistry.registerModules([AllCommunityModule]);

import type { DataTableConfig } from '../../../../types/dashboard';
import type { RenderHandle, RenderConfig } from '../../../../types/dashboard';
import { dashboardGridTheme } from '../theme';
import { formatValue } from '../formatters';
import { DASHBOARD_TOKENS } from '../tokens';

export function renderAgGrid(
  containerId: string,
  blockId: string,
  config: DataTableConfig,
  data: Record<string, unknown>[]
): RenderHandle {
  const container = document.getElementById(containerId);
  if (!container) {
    throw new Error(`Container element not found: ${containerId}`);
  }

  const columnDefs: ColDef[] = config.columns.map((col) => {
    // AG Grid interprets dots in `field` as nested property paths, but the
    // backend aliases grouped columns with their full "table.column" key as a
    // flat string (e.g. "transaction_details.line_category"). Use valueGetter
    // with bracket notation so the dot is treated literally.
    const hasDotKey = col.field.includes('.');

    return {
      field: hasDotKey ? undefined : col.field,
      valueGetter: hasDotKey
        ? (params: { data: Record<string, unknown> }) => params.data?.[col.field]
        : undefined,
      headerName: col.header,
      width: col.width,
      pinned: col.pinned,
      sortable: col.sortable !== false,
      resizable: true,
      valueFormatter: col.format && col.format !== 'text'
        ? (params: { value: unknown }) => formatValue(params.value, col.format!, col.currency)
        : undefined,
      cellStyle: col.conditionalColor
        ? (params: { value: unknown }) => {
            const val = Number(params.value);
            if (isNaN(val)) return undefined;
            if (val > 0) return { color: DASHBOARD_TOKENS.green };
            if (val < 0) return { color: DASHBOARD_TOKENS.red };
            return { color: DASHBOARD_TOKENS.textMuted };
          }
        : undefined,
    };
  });

  const gridOptions: GridOptions = {
    theme: dashboardGridTheme,
    columnDefs,
    rowData: data,
    domLayout: 'autoHeight',
    defaultColDef: {
      sortable: true,
      resizable: true,
    },
    animateRows: true,
    rowHeight: config.rowHeight ?? 40,
    headerHeight: 38,
    suppressCellFocus: true,
  };

  const gridApi = createGrid(container, gridOptions);

  if (config.defaultSort) {
    gridApi.applyColumnState({
      state: [{ colId: config.defaultSort.field, sort: config.defaultSort.direction }],
      defaultState: { sort: null },
    });
  }

  let currentData = data;

  const handle: RenderHandle = {
    blockId,
    blockType: 'data-table',
    containerId,
    update(newData: Record<string, unknown>[]) {
      currentData = newData;
      gridApi.setGridOption('rowData', newData);
    },
    rerender(newConfig: RenderConfig) {
      gridApi?.destroy();
      const rebuilt = renderAgGrid(containerId, blockId, newConfig as DataTableConfig, currentData);
      handle.update = rebuilt.update;
      handle.rerender = rebuilt.rerender;
      handle.dispose = rebuilt.dispose;
    },
    dispose() {
      gridApi?.destroy();
    },
  };

  return handle;
}
